import React, { JSX, Suspense, use } from "react";
import { ColumnConfig, TextAlignment, wafflesKeyColumnName, WafflesThemes } from "@app/shared/waffles/exports";
import { NetworkAssessmentAnalysisType, NPIStatus } from "@app/projects/enumerators";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { getEntityIconClasses, getEntityTypeDescription } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { PciDashboardProvider } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardProvider";
import { PciDashboardProviderPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardProviderPayload";
import { PciDashboardProviderAddress } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardProviderAddress";
import { CareJourneyScoresEpisode, ServicingProviderDetail } from "@app/projects/results/servicing-provider/servicingProviderDetail"
import { CareJourneyConditionTableData } from "@app/projects/servicing-provider/CareJourneyScores";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import PciDashboardService from "@app/projects/network-assessment/pci-dashboard/shared/pciDashboardService";
import ProjectOptions from "@app/projects/projectOptions";
import ClaimsProviderInsights from "@app/projects/servicing-provider/ClaimsProviderInsights";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { formatUnconvertedDateCustom } from "@app/shared/formatDateTime";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { valueOrDashes } from "@app/shared/utility/valueOrDashes";
import { Waffles } from "@app/shared/waffles/Waffles";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

interface PciDashboardProviderProfileProps {
  isProjected?: boolean;
  isTerminated?: boolean;
  netNewProvider?: boolean;
  nextClosestProvider?: boolean;
  project: ProjectOptions;
  providerId: number;
  providerName: string;
  runConfiguration: ProjectRunConfigurationResult;
  providerNetworkType: ProviderNetworkTypeEnum;
  specialtyGroupCode: string;
  specialtyGroupName: string;
  countySSA: string,
  npi: string
}

type WrappedPciDashboardProviderProfileProps = { providerDetailPromise: Promise<PciDashboardProvider> } & PciDashboardProviderProfileProps

interface PciDashboardFlyoutAddressTableData {
  associatedAddress: string | JSX.Element;
  county: string | JSX.Element;
  countySSA: string | JSX.Element;
  latitude: string | JSX.Element;
  longitude: string | JSX.Element;
  [wafflesKeyColumnName]: string;
}

interface PciDashboardFlyoutCareJourneyConditionTableData {
  costScore: string | JSX.Element;
  episodeName: string | JSX.Element;
  numberOfEpisodes: string | JSX.Element;
  qualityScore: string | JSX.Element;
  [wafflesKeyColumnName]: string;
}

const addressColumnConfig: ColumnConfig = {
  associatedAddress: {
    alignment: TextAlignment.Left,
    displayText: "Associated Addresses",
    noSort: true,
    testId: "pci-flyout-addresses",
  },
  county: {
    alignment: TextAlignment.Left,
    displayText: "County",
    noSort: true,
    testId: "pci-flyout-county",
  },
  countySSA: {
    alignment: TextAlignment.Left,
    displayText: "County SSA",
    noSort: true,
    testId: "pci-flyout-county-ssa",
  },
  latitude: {
    alignment: TextAlignment.Left,
    displayText: "Latitude",
    noSort: true,
    testId: "pci-flyout-latitude",
  },
  longitude: {
    alignment: TextAlignment.Left,
    displayText: "Longitude",
    noSort: true,
    testId: "pci-flyout-longitude",
  }
};

const careJourneyColumnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  episodeName: {
    alignment: TextAlignment.Left,
    displayText: "Episode Name",
    noSort: true,
    testId: "pci-flyout-episode-name",
  },
  numberOfEpisodes: {
    alignment: TextAlignment.Left,
    displayText: "Number of Episodes",
    noSort: true,
    testId: "pci-flyout-number-of-episodes",
  },
  costScore: {
    alignment: TextAlignment.Left,
    displayText: "Cost Score",
    noSort: true,
    testId: "pci-flyout-cost-score",
  },
  qualityScore: {
    alignment: TextAlignment.Left,
    displayText: "Quality Score",
    noSort: true,
    testId: "pci-flyout-quality-score",
  }
  /* oxlint-enable sort-keys */
};

function mapAddressesToTableData(addresses: PciDashboardProviderAddress[]): PciDashboardFlyoutAddressTableData[] {
  return addresses.map((address, index) => {
    const row: PciDashboardFlyoutAddressTableData = {
      /* oxlint-disable sort-keys */
      associatedAddress: <>{valueOrDashes(address.address)} {!!address.address2 ? <><br />{address.address2}</> : null} <br /> {address.city}{!!address.state ? <>, {address.state} {address.zip}</> : null}</>,
      county: <>{valueOrDashes(address.countyName)}</>,
      countySSA: <>{valueOrDashes(address.countySSA)}</>,
      longitude: <>{valueOrDashes(address.latitude)}</>,
      latitude: <>{valueOrDashes(address.longitude)}</>,
      key: index.toString()
      /* oxlint-enable sort-keys */
    }
    return row;
  });
}

function mapCareJourneyEpisodesToTableData(episodes: CareJourneyScoresEpisode[]): CareJourneyConditionTableData[] {
  return episodes.map((episode, index) => {
    const row: PciDashboardFlyoutCareJourneyConditionTableData = {
      /* oxlint-disable sort-keys */
      episodeName: <>{valueOrDashes(episode.name)}</>,
      numberOfEpisodes: <>{valueOrDashes(episode.count)}</>,
      costScore: <>{valueOrDashes(episode.costScore)}/5</>,
      qualityScore: <>{valueOrDashes(episode.qualityScore)}/5</>,
      key: index.toString()
      /* oxlint-enable sort-keys */
    }
    return row;
  });
}

function renderLoading(providerName: string) {
  return (
    <div className="row">
      <div className="col-12 text-center">
        <span>
          <i className="fa fa-spinner fa-spin" /> Loading additional information for <strong>{providerName}</strong>...
        </span>
      </div>
    </div>
  );
}

function renderNpiStatus(npiStatus: NPIStatus) {
  switch (npiStatus) {
    case NPIStatus.Deactivated:
    case NPIStatus.Invalid:
      return (<small className="text-danger pl5"><i className="fa fa-times supress-panel-styles" /> {npiStatus}</small>);
    case NPIStatus.Missing:
    case NPIStatus.Unknown:
      return (<small className="text-warning pl5"><i className="fa fa-question supress-panel-styles" /> {npiStatus}</small>);
    case NPIStatus.Valid:
      return (<small className="text-success pl5"><i className="fa fa-check supress-panel-styles" /> {npiStatus}</small>);
  }
}

function renderAttestationStatus(lastAttestedDate?: Date) {
  return (
    <>
      <br />
      <strong>Last Attested Date</strong> {lastAttestedDate ? formatUnconvertedDateCustom(lastAttestedDate, "L") : "---"}
    </>
  );
}

const WrappedPciDashboardProviderFlyout: React.FC<WrappedPciDashboardProviderProfileProps> = (props) => {
  const record = use(props.providerDetailPromise);

  if (record.providerId === 0) {
    record.npi = props.npi;
    record.providerId = props.providerId;
    record.displayName = props.providerName
    record.specialtiesDisplay = [props.specialtyGroupCode + " - " + props.specialtyGroupName];

    record.addresses = [];
    record.careJourneyScoresTopEpisodes = [];
  }

  const addressTableData = mapAddressesToTableData(record.addresses ?? []);
  const careJourneyTableData = mapCareJourneyEpisodesToTableData(record.careJourneyScoresTopEpisodes ?? []);

  const showAttestation = (props.providerNetworkType !== ProviderNetworkTypeEnum.Market) && props.project.showAttestation;

  return (
    <div className="servicing-provider-profile">
      <section className="intro">
        <div className="row">
          <div className="col-sm-3 mb-3">
            <i className={getEntityIconClasses(record.entityType)} />
          </div>

          <div className="col-sm-9 mb-3">
            <h1 className="pl0-imp">
              {record.displayName}
              {record.credentials ? `, ${record.credentials}` : ""}
            </h1>
            {record.entityType ?
              <div>
                <strong className="text-muted">{getEntityTypeDescription(record.entityType)}</strong>
                <br />
              </div> :
              null
            }
            {record.addresses[0]?.address ? <>{record.addresses[0]?.address}<br /></> : null}
            {record.addresses[0]?.address2 ? <>{record.addresses[0]?.address2}<br /></> : null}
            {record.addresses[0]?.city}
            {record.addresses[0]?.city && (record.addresses[0]?.state || record.addresses[0]?.zip) ? ", " : null}
            {record.addresses[0]?.state} {record.addresses[0]?.zip}<br />
            <strong>NPI Status</strong> {record.npiStatus ? renderNpiStatus(record.npiStatus) : null}
            {showAttestation && renderAttestationStatus(record.lastAttestedDate)}
          </div>
        </div>
      </section>

      <AccordionContainer>
        <Accordion title="General" defaultOpen={true} testId="pci-dashboard-provider-profile__general-accordion">
          <div className="data-box col-12 col-sm-6 col-md-4">
            <div className="data-box-header">NPI</div>
            <div className="data-box-content">
              {valueOrDashes(record.npi)}
            </div>
          </div>
          <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
            <div className="data-box col-12 col-sm-6 col-md-4">
              <div className="data-box-header">TIN</div>
              <div className="data-box-content">
                {valueOrDashes(record.tin)}
              </div>
            </div>
          </RoleRestricted>
          <div className="data-box col-12 col-sm-6 col-md-4">
            <div className="data-box-header">First</div>
            <div className="data-box-content">
              {valueOrDashes(record.firstName)}
            </div>
          </div>
          <div className="data-box col-12 col-sm-6 col-md-4">
            <div className="data-box-header">Middle</div>
            <div className="data-box-content">
              {valueOrDashes(record.middleName)}
            </div>
          </div>
          <div className="data-box col-12 col-sm-6 col-md-4">
            <div className="data-box-header">Last</div>
            <div className="data-box-content">
              {valueOrDashes(record.lastName)}
            </div>
          </div>

          <div className="data-box col-12 col-sm-6 col-md-4">
            <div className="data-box-header">Taxonomy</div>
            <div className="data-box-content">
              {valueOrDashes(record.taxonomyCode)}
            </div>
          </div>
          <div className="data-box col-12 col-sm-6 col-md-4">
            <div className="data-box-header">Specialt{record.specialtiesDisplay.length > 1 ? "ies" : "y"}</div>
            <div className="data-box-content">
              <ul className="list-unstyled">
                {record.specialtiesDisplay.map((s) => <li key={s}>{s}</li>)}
              </ul>
            </div>
          </div>
        </Accordion>
        <Accordion title="Address / Location" testId="pci-dashboard-provider-profile__address-location-accordion">
          <Waffles<PciDashboardFlyoutAddressTableData>
            columns={addressColumnConfig}
            data={addressTableData}
            disableStickyHeader={true}
            fullWidth={true}
            hideTableHead={false}
            keyColumns={["type"]}
            testId={`pci-dashboard-provider-profile__address-location-table`}
            theme={WafflesThemes.Simple}
          />
        </Accordion>
        {props.project.showCareJourneyScores && (
          <Accordion title={
            <>
              <span className="right-padding">CareJourney Provider Performance</span>
              <img src={`${window.customUIBaseUrl}/images/carejourney-logo.svg`} height="21" />
            </>
          } testId="pci-dashboard-provider-carejourney-accordion">
            <div>
              <div className="data-box">
                <span className="data-box-header">Cost Score: </span>
                <span className="data-box-content">{valueOrDashes(record.careJourneyScoresCostScore)}/5</span>
              </div>
            </div>
            <div>
              <div className="data-box">
                <span className="data-box-header">Quality Score: </span>
                <span className="data-box-content">{valueOrDashes(record.careJourneyScoresQualityScore)}/5</span>
              </div>
            </div>
            <h6>Episode Cost and Quality</h6>
            <Waffles<PciDashboardFlyoutCareJourneyConditionTableData>
              columns={careJourneyColumnConfig}
              data={careJourneyTableData}
              disableStickyHeader={true}
              fullWidth={true}
              hideTableHead={false}
              keyColumns={["type"]}
              testId={`pci-dashboard-provider-profile__carejourney-location-table`}
              theme={WafflesThemes.Simple}
            />
          </Accordion>
        )}
        <Accordion title="Claims" testId="pci-dashboard-provider-profile__claims-accordion">
          {
            props.project.showPciData ? (
              <ClaimsProviderInsights showTricare={props.project.showTricareClaimsVolumeColumn} showVA={props.project.showVaClaimsVolumeColumn} record={record as unknown as ServicingProviderDetail} />
            ) : (
              <QESAlert classNames="text-transform-none mb-0" type="info" persist={true}>
                Quest can deliver powerful provider performance insights through the <b>Provider Claims Insights</b> package. Please contact your sales representative. <br /> <br />
                Insights include: <br />
                <ul className="mb-0">
                  <li>
                    Provider effectiveness (quality) and efficiency (cost) scores
                  </li>
                  <li>
                    Provider activity by Line of Business
                  </li>
                  <li>
                    Provider payer relationships
                  </li>
                  <li>
                    Provider top conditions and procedures
                  </li>
                </ul>
              </QESAlert>
            )}
        </Accordion>
      </AccordionContainer>
    </div>
  );
}

export const PciDashboardProviderFlyout: React.FC<PciDashboardProviderProfileProps> = (props) => {
  async function getProviderDetail(): Promise<PciDashboardProvider> {
    const providerPayload: PciDashboardProviderPayload = {
      countySSA: props.countySSA,
      networkAssessmentAnalysisType: props.runConfiguration.networkAssessmentAnalysisType || NetworkAssessmentAnalysisType.ServiceArea,
      npi: props.npi,
      specialtyGroupCode: props.specialtyGroupCode
    }

    return new PciDashboardService().getServicingProviderDetail(
      props.project.projectId,
      props.runConfiguration.projectRunConfigurationId,
      props.providerNetworkType,
      props.providerId,
      providerPayload);
  }

  return (
    <Suspense fallback={renderLoading(props.providerName)}>
      <WrappedPciDashboardProviderFlyout {...props} providerDetailPromise={getProviderDetail()} />
    </Suspense>
  );
}

export default PciDashboardProviderFlyout;
